"use client";
import { FullWidthSliderOptionsType } from "@/types/ModuleOptionsType";
import { TouchEvent, useEffect, useState } from "react";
import styles from "./FullWidthSlider.module.scss";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavigation,
} from "@aleksundshantu/aunds-utils-react/primitives/carousel";
import { BreakpointsOptionsType } from "@/getData/getBreakpoints";
import { getItemAttributes } from "@/components/_primitives/utils/getItemAttributes";
import { Section } from "@/components/Section/Section";
import { FadeInUp } from "@/components/Animation/ScrollAnimation";
import {
  FullWidthSliderItemType,
  FullWidthSliderModuleType,
} from "./FullWidthSlider.server";
export type { FullWidthSliderItemType, FullWidthSliderModuleType };
interface FullWidthSliderProps {
  data: FullWidthSliderModuleType;
  fullWidthSliderOptions: FullWidthSliderOptionsType;
  breakpoints: BreakpointsOptionsType | null;
  posts: FullWidthSliderItemType[];
  sliderItems: {
    [k: string]: JSX.Element;
  };
}
/* interface AnimatedContentProps {
  direction: RowDirectionType;
  children: ReactNode;
} */

const FullWidthSlider = ({
  data,
  fullWidthSliderOptions,
  sliderItems,
  posts,
}: FullWidthSliderProps) => {
  const {
    background_color_class,
    id,
    row_direction_class,
    section_margin_class,
  } = data.layout;

  const {
    min_height,
    slider_options: { slider_navigation },
  } = fullWidthSliderOptions;

  const itemHeight = {
    ...(min_height?.mobile
      ? { mobile: min_height.mobile.toString() + "px" }
      : {}),
    ...(min_height?.tablet
      ? { tablet: min_height.tablet.toString() + "px" }
      : {}),
    ...(min_height?.desktop
      ? { desktop: min_height.desktop.toString() + "px" }
      : {}),
    ...(min_height?.fullhd
      ? { fullhd: min_height.fullhd.toString() + "px" }
      : {}),
  };
  const itemAttr = getItemAttributes({
    itemHeight,
    itemAttrFor: "textmedia-slider",
  });

  const [direction, setDirection] = useState<{
    index: number;
    direction: "+" | "-";
  }>({ index: 0, direction: "+" });

  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(direction.index);
  }, [direction]);

  const NAV_JSX =
    posts.length > 1 ? (
      <CarouselNavigation
        type={slider_navigation}
        className={`${styles["nav"]} ${slider_navigation}`}
      />
    ) : (
      <></>
    );
  const textMediaJSX = (
    <Carousel
      loop
      hasFade
      className={`${styles["full-width-slider-carousel"]} `}
      align={"center"}
    >
      <CarouselContent
        spacing="0"
        outerPadding="0"
        extraClasses={`${styles["full-width-slider-carousel"]}`}
      >
        {posts.length > 0 && (
          <>
            {posts.map((p, key) => {
              return (
                <CarouselItem
                  key={key}
                  className={`columns is-multiline is-mobile ${row_direction_class} ${styles["full-width-slider-element"]} is-box ${itemAttr.className}`}
                  data-active={index === key}
                  itemWidth={{
                    mobile: "100%",
                  }}
                >
                  {sliderItems[p.id]}
                </CarouselItem>
              );
            })}
          </>
        )}
      </CarouselContent>
      {NAV_JSX}
    </Carousel>
  );

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // the required distance between touchStart and touchEnd to be detected as a swipe
  const minSwipeDistance = 50;

  const handleSwipe = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      setDirection({
        index: index - 1,
        direction: "-",
      });
    }

    if (isRightSwipe) {
      setDirection({
        index: index + 1,
        direction: "+",
      });
    }
  };

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: TouchEvent<HTMLDivElement>) =>
    setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    handleSwipe();
  };

  return (
    <Section
      className={`${styles["full-width-slider"]} text-media full-width ${background_color_class} ${section_margin_class} px-0`}
      disableColumns
      disableContainer
      id={id}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        {...itemAttr}
        className={` ${styles["full-width-slider-columns"]}  ${row_direction_class === "is-row-reverse-desktop" ? "is-row-reverse-desktop" : ""}`}
      >
        <FadeInUp>{textMediaJSX}</FadeInUp>
      </div>
    </Section>
  );
};

export default FullWidthSlider;
